import { ComponentProps, forwardRef, useState } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import type Select from 'react-select';

import Chevron from '~/icons/chevron-down.svg';
import { IImageMedia } from '~/lib/data-contract';
import { Icon } from '~/shared/components/Icon/Icon';
import { ResponsiveImageMedia } from '~/shared/components/ResponsiveImageMedia/ResponsiveImageMedia';

import styles from './altSelector.module.css';

import { InvalidMessage } from '../InvalidMessage/InvalidMessage';

export type AltSelectorOption = {
	label: string;
	value: string;
	icon?: IImageMedia;
};

export type AltSelectorProps = ComponentProps<typeof Select<AltSelectorOption, false>> & {
	label?: string;
	isInvalid?: boolean;
	invalidMessage?: string;
};

const ReactSelect = dynamic(() => import('react-select'), { ssr: false }) as typeof Select;

const iconSizes = '50px';

export const AltSelector = forwardRef<any, AltSelectorProps>((props, ref) => {
	const [isOpen, setIsOpen] = useState(false);
	const { label, isInvalid, invalidMessage, ...rest } = props;

	return (
		<ReactSelect
			{...rest}
			ref={ref}
			isSearchable={false}
			onMenuOpen={() => setIsOpen(true)}
			onMenuClose={() => setIsOpen(false)}
			components={{
				IndicatorSeparator: () => null,
				IndicatorsContainer: ({ children, className, ...props }) => {
					return (
						<div
							className={clsx(styles.indicatorContainer, className)}
							{...props.innerProps}
						>
							{children}
						</div>
					);
				},
				DropdownIndicator: (props) => {
					return (
						<div {...props.innerProps}>
							<Icon
								className={clsx(styles.dropdownIndicator, isOpen && styles.open)}
								size="md"
							>
								<Chevron />
							</Icon>
						</div>
					);
				},
				Control: ({ children, className, ...props }) => {
					return (
						<div
							className={clsx(styles.control, className)}
							ref={props.innerRef}
							{...props.innerProps}
						>
							{children}
						</div>
					);
				},
				SelectContainer: ({ children, className, ...props }) => {
					return (
						<div>
							<div
								className={clsx(styles.rootContainer, { [styles.focus]: props.isFocused, [styles.invalid]: isInvalid }, className)}
								{...props.innerProps}
							>
								{label ? (
									<label className={clsx(styles.selectorLabel, isInvalid && styles.invalid)}>
										<span className={styles.selectorLabelInner}>{label}</span>
									</label>
								) : null}
								{children}
							</div>
							{isInvalid && invalidMessage ? <InvalidMessage>{invalidMessage}</InvalidMessage> : null}
						</div>
					);
				},
				Option: ({ children, className, ...props }) => {
					const { icon } = props.data;

					return (
						<div
							className={clsx(styles.option, props.isFocused && styles.focus, className)}
							ref={props.innerRef}
							{...props.innerProps}
						>
							{icon ? (
								<div className={styles.iconWrapper}>
									<ResponsiveImageMedia
										sizes={iconSizes}
										image={icon}
									/>
								</div>
							) : null}
							<span>{children}</span>
						</div>
					);
				},
				Menu: ({ children, className, ...props }) => {
					return (
						<div
							className={clsx(styles.menu, className)}
							{...props.innerProps}
							ref={props.innerRef}
						>
							{children}
						</div>
					);
				},
				MenuList: ({ children }) => {
					return <div>{children}</div>;
				},
				SingleValue: ({ children, className, ...props }) => {
					const { icon } = props.data;

					return (
						<div className={clsx(styles.singleValue, className)}>
							{icon ? (
								<div className={styles.iconWrapper}>
									<ResponsiveImageMedia
										sizes={iconSizes}
										image={icon}
									/>
								</div>
							) : null}
							{children}
						</div>
					);
				},
				ValueContainer: ({ children }) => {
					return <div className={styles.valueContainer}>{children}</div>;
				},
				Placeholder: ({ children, className, ...props }) => {
					return (
						<div
							className={clsx(styles.singleValue, className)}
							{...props.innerProps}
						>
							{children}
						</div>
					);
				},
			}}
		/>
	);
});

AltSelector.displayName = 'AltSelector';
